/* ═══════════════════════════════════════════════════════════════
   The Master Forma Studio — the forma's data life.

   Unlike a project's forma draft (`designer/useFormaDraft.ts`, which
   autosaves silently and forks a project copy on first edit), a MASTER
   is a template: explicit Save, like the classic editor. There is no
   copy-on-write here — a master edited in the Studio IS the master.

   Undo is a 100-deep snapshot stack, mirroring useFormaDraft's model,
   registered into the workspace's undo coordinator so Ctrl+Z behaves
   identically to every other SpecForma surface (contract §2.7).
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import type { StandardConfig } from '../../shared/types';
import { FormaService } from '../services/formaService';
import { pushEditHistory } from '../../shared/editHistory';
import { reportError } from '../services/errorLog';

const UNDO_LIMIT = 100;

export type StudioLoadState = 'loading' | 'ready' | 'error' | 'not-found';

export interface MasterFormaDraft {
  loadState: StudioLoadState;
  error: string | null;
  forma: StandardConfig | null;
  dirty: boolean;
  saving: boolean;
  edit: (fn: (f: StandardConfig) => StandardConfig) => void;
  save: () => Promise<StandardConfig | undefined>;
  undo: () => boolean;
  redo: () => boolean;
  canUndo: boolean;
  canRedo: boolean;
  reload: () => void;
  /** Import-as-draft (contract §3 R6, item 1): replace the whole in-memory
   *  draft with a harvested config — a NEW, unsaved forma (fresh id, no
   *  master/project lineage), exactly like the classic editor's
   *  handleImport. Nothing lands in the Formas library until Save. */
  importDraft: (cfg: StandardConfig) => void;
}

export function useMasterFormaDraft(
  formaId: string | null,
  userName: string,
  userId: string | undefined,
  /** Present only for a brand-new forma with nothing saved yet (contract
   *  §3 R6, item 10a) — the Studio skips the list lookup and starts from
   *  this in-memory config instead (CSI-seeded by the caller). */
  seed?: StandardConfig | null
): MasterFormaDraft {
  const [loadState, setLoadState] = React.useState<StudioLoadState>('loading');
  const [error, setError] = React.useState<string | null>(null);
  const [forma, setForma] = React.useState<StandardConfig | null>(null);
  const [dirty, setDirty] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [, tick] = React.useReducer((n: number) => n + 1, 0);

  const undoRef = React.useRef<StandardConfig[]>([]);
  const redoRef = React.useRef<StandardConfig[]>([]);
  const formaRef = React.useRef<StandardConfig | null>(null);
  const reloadTokenRef = React.useRef(0);

  const load = React.useCallback(() => {
    if (seed) {
      formaRef.current = seed;
      setForma(seed);
      undoRef.current = [];
      redoRef.current = [];
      setDirty(false);
      setLoadState('ready');
      return;
    }
    if (!formaId) {
      setLoadState('error');
      setError('No forma to open.');
      return;
    }
    const token = ++reloadTokenRef.current;
    setLoadState('loading');
    setError(null);
    FormaService.list()
      .then((all) => {
        if (reloadTokenRef.current !== token) return;
        const match = all.find((f) => f.id === formaId);
        if (!match) {
          setLoadState('not-found');
          return;
        }
        formaRef.current = match;
        setForma(match);
        undoRef.current = [];
        redoRef.current = [];
        setDirty(false);
        setLoadState('ready');
      })
      .catch((err: unknown) => {
        if (reloadTokenRef.current !== token) return;
        reportError('Studio:loadForma', err);
        setError(err instanceof Error ? err.message : String(err ?? 'Could not load the forma'));
        setLoadState('error');
      });
  }, [formaId, seed]);

  React.useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formaId, seed]);

  // ─── Legacy migration: {EndOfSection} living in a post-body zone ───
  // Old formas stored the end-of-section line as a TOKEN in postLeft/Center/
  // Right. The end-of-section is its own toggle now, so leaving the token
  // there prints the line twice. The classic canvas migrated this on mount
  // (PageCanvas's first effect); the Studio must do the same or the same
  // forma behaves differently in the two editors. Silent, idempotent, and
  // NOT an undo step or a dirty edit — it is a read-time normalisation.
  React.useEffect(() => {
    const cur = forma;
    if (!cur) return;
    const sp = cur.special as unknown as Record<string, string[] | undefined>;
    const keys = ['postLeft', 'postCenter', 'postRight'] as const;
    const isEos = (t: string) => t.replace(/\|.*$/, '') === '{EndOfSection}';
    if (!keys.some((k) => (sp?.[k] || []).some(isEos))) return;
    const next: StandardConfig = {
      ...cur,
      special: {
        ...cur.special,
        postLeft: (sp.postLeft || []).filter((t) => !isEos(t)),
        postCenter: (sp.postCenter || []).filter((t) => !isEos(t)),
        postRight: (sp.postRight || []).filter((t) => !isEos(t)),
        eosEnabled: true,
      },
    };
    formaRef.current = next;
    setForma(next);
    tick();
  }, [forma]);

  const edit = React.useCallback((fn: (f: StandardConfig) => StandardConfig) => {
    const cur = formaRef.current;
    if (!cur) return;
    undoRef.current.push(cur);
    if (undoRef.current.length > UNDO_LIMIT) undoRef.current.shift();
    redoRef.current = [];
    const next = fn(cur);
    formaRef.current = next;
    setForma(next);
    setDirty(true);
    tick();
  }, []);

  const undo = React.useCallback((): boolean => {
    const prev = undoRef.current.pop();
    if (!prev || !formaRef.current) return false;
    redoRef.current.push(formaRef.current);
    formaRef.current = prev;
    setForma(prev);
    setDirty(true);
    tick();
    return true;
  }, []);

  const redo = React.useCallback((): boolean => {
    const next = redoRef.current.pop();
    if (!next || !formaRef.current) return false;
    undoRef.current.push(formaRef.current);
    formaRef.current = next;
    setForma(next);
    setDirty(true);
    tick();
    return true;
  }, []);

  // Whether this draft has ever been persisted — drives the History
  // button's first entry label ("created" vs "edited"), same distinction
  // the classic editor makes at save time.
  const savedOnceRef = React.useRef(!seed);

  const save = React.useCallback(async (): Promise<StandardConfig | undefined> => {
    const cur = formaRef.current;
    if (!cur) return undefined;
    setSaving(true);
    try {
      const action = savedOnceRef.current ? ('edited' as const) : ('created' as const);
      const toSave: StandardConfig = {
        ...cur,
        editHistory: pushEditHistory(cur.editHistory, userName, userId, action),
      };
      const saved = await FormaService.save(toSave);
      savedOnceRef.current = true;
      formaRef.current = saved;
      setForma(saved);
      setDirty(false);
      return saved;
    } catch (err) {
      reportError('Studio:saveForma', err);
      throw err;
    } finally {
      setSaving(false);
    }
  }, [userName, userId]);

  const importDraft = React.useCallback((cfg: StandardConfig) => {
    const draft: StandardConfig = {
      ...cfg,
      id: 'new-' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6),
      projectId: undefined,
      sourceFormaId: undefined,
      specId: undefined,
      attachedProjectId: undefined,
      editHistory: undefined,
    };
    savedOnceRef.current = false;
    formaRef.current = draft;
    setForma(draft);
    undoRef.current = [];
    redoRef.current = [];
    setDirty(true);
    setLoadState('ready');
    tick();
  }, []);

  return {
    loadState,
    error,
    forma,
    dirty,
    saving,
    edit,
    save,
    undo,
    redo,
    canUndo: undoRef.current.length > 0,
    canRedo: redoRef.current.length > 0,
    reload: load,
    importDraft,
  };
}
